


































































import Vue from 'vue';
import { maxLength, minLength, required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import CurrentUserQuery from '@/components/partials/war-room/WarRoomEditProfile/apollo/currentUser.query';
import CheckIcon from './images/check.vue.svg';
import DogIcon from './images/dog.vue.svg';
import { SourceAction } from './types';
import type { WarRoomModalParams } from './types';
import Modal from '~/components/common/Modal/Modal.vue';
import WarRoomAvatarUploader from '~/components/partials/war-room/WarRoomAvatarUploader/WarRoomAvatarUploader.vue';
import BravadoTextInputV3 from '~/components/common/BravadoTextInputV3/BravadoTextInputV3.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import WarRoomProfileHints from '~/components/partials/war-room/WarRoomProfileHints.vue';
import type { FormState } from '~/types/common';
import CloseIcon from '~/assets/svg/close.vue.svg';

let validationTimeout: number | null = null;

interface IData {
  form: {
    username: string;
    war_room_position: string;
    war_room_avatar: File | null;
  };
  attemptedSubmission: boolean;
  loading: FormState;
  isPersistentModal: boolean;
}

export default Vue.extend({
  name: 'WarRoomUsernameAndAvatarModal',
  components: {
    WarRoomProfileHints,
    WarRoomAvatarUploader,
    Modal,
    CheckIcon,
    DogIcon,
    BravadoTextInputV3,
    BravadoButtonV3,
    CloseIcon,
  },
  mixins: [validationMixin],
  data(): IData {
    return {
      form: {
        username: this.$auth.user?.username!,
        war_room_position: this.$auth.user?.war_room_position!,
        war_room_avatar: null,
      },
      attemptedSubmission: false,
      loading: 'idle',
      isPersistentModal: false,
    };
  },
  validations: {
    form: {
      username: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(30),
        isValid(value) {
          return /^[\w](?!.*?\.{2})[\w.]{1,28}[\w]$/.test(value);
        },
        isUnique(value) {
          if (this.$v.form.username!.minLength && this.$v.form.username!.required && this.$v.form.username!.isValid) {
            return new Promise(resolve => {
              if (validationTimeout) {
                window.clearTimeout(validationTimeout);
                validationTimeout = null;
              }

              const username = encodeURIComponent(value);

              validationTimeout = window.setTimeout(async () => {
                try {
                  await this.$api.$get(`/api/war_room/users/check_username?username=${username}`);
                  resolve(true);
                } catch (e) {
                  resolve(false);
                }
              }, 500);
            });
          } else {
            return true;
          }
        },
      },
      war_room_position: { required },
    },
  },
  computed: {
    isShowCheckIcon(): boolean {
      return !this.$v.form.username!.$invalid;
    },
    modalClasses(): Record<string, boolean> {
      return {
        'username-and-avatar-modal': true,
        'username-and-avatar-modal--failed': !this.isValid && this.attemptedSubmission,
      };
    },
    formHasAvatar(): boolean {
      return Boolean(this.form.war_room_avatar);
    },
    isValid(): boolean {
      return !this.$v.form.$invalid;
    },
    isButtonDisabled(): boolean {
      return !this.isValid || this.loading !== 'idle';
    },
    usernameErrorMessage(): string {
      if (!this.$v.form.username!.required) return 'Username is required';
      if (!this.$v.form.username!.isUnique && !this.$v.form.username!.$pending) return 'The username already exists';
      if (!this.$v.form.username!.minLength) return 'The username is too short';
      if (!this.$v.form.username!.maxLength) return 'The username is too long';
      if (!this.$v.form.username!.isValid) return 'Wrong characters';

      return '';
    },
    currentTitleErrorMessage(): string {
      if (!this.$v.form.war_room_position!.required) return 'Title is required';

      return '';
    },
  },
  methods: {
    handleFileChange(file: File): void {
      this.form.war_room_avatar = file;
    },
    isFieldValid(fieldName: string): boolean {
      const field = this.$v.form[fieldName];
      return !(field!.$dirty && field!.$invalid);
    },
    buttonCopy(params: WarRoomModalParams): string {
      switch (params.source) {
        case SourceAction.comment:
          return 'Continue to comment';
        case SourceAction.post:
          return 'Continue to post';
        default:
          return 'Continue';
      }
    },
    async handleSubmit(): Promise<void> {
      if (!this.isValid) {
        this.attemptedSubmission = true;
        return;
      }

      this.loading = 'submitting';
      const formData = new FormData();
      formData.append('user[username]', this.form.username);
      formData.append('user[war_room_position]', this.form.war_room_position);
      if (this.formHasAvatar) {
        formData.append('user[war_room_avatar]', this.form.war_room_avatar as File);
      }

      try {
        await this.$api.$put('/api/war_room/user.json', formData);
        this.$analytics.logEvent('global__username-avatar__profile__update');
        await Promise.all([
          this.$auth.fetchUser(),
          this.$apollo.getClient().queryManager.watchQuery({ query: CurrentUserQuery }).refetch(),
        ]); // refresh user session data
        this.$emit('on-submit');
        this.$modal.hide(this.$constants.dynamicModals.WAR_ROOM_USERNAME_AVATAR_MODAL);
      } catch (e) {
        this.$notice.error('Something went wrong', e);
      }

      this.loading = 'idle';
    },
  },
});
